/**
 * Product Also Available
 * - Collapsed by default
 * - One-shot expansion when navigating from any group member
 * - No persistent expanded/collapsed memory
 * - Delegated events for better scaling
 * - Hover/focus updates the style label temporarily
 */

(function () {
  const SELECTORS = {
    component: "[data-also-available]",
    gridWrap: "[data-also-available-grid-wrap]",
    toggle: "[data-also-available-toggle]",
    toggleText: "[data-also-available-toggle-text]",
    link: "[data-also-available-link]",
    item: "[data-also-available-item]",
    style: "[data-also-available-style]",
    styleValue: "[data-also-available-style-value]",
    currentProduct: "[data-current-product='true']"
  };

  const CLICKED_STATE_TTL_MS = 15 * 60 * 1000;

  function getStorageKey(groupKey, suffix) {
    return `productAlsoAvailable_${suffix}_${groupKey}`;
  }

  function getSessionItem(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function setSessionItem(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // sessionStorage can be blocked in some privacy contexts.
    }
  }

  function removeSessionItem(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      // sessionStorage can be blocked in some privacy contexts.
    }
  }

  function parseClickedState(rawValue) {
    if (!rawValue) return null;

    try {
      const parsed = JSON.parse(rawValue);

      if (!parsed || typeof parsed !== "object") return null;

      return parsed;
    } catch (error) {
      return null;
    }
  }

  function getCurrentProductHandle(component) {
    const handleFromRoot = (component.dataset.currentProductHandle || "").trim();

    if (handleFromRoot) return handleFromRoot;

    const currentProduct = component.querySelector(SELECTORS.currentProduct);

    return currentProduct
      ? (currentProduct.getAttribute("data-product-handle") || "").trim()
      : "";
  }

  function getLinkFromEvent(event, component) {
    const target = event.target;

    if (!target || typeof target.closest !== "function") return null;

    const link = target.closest(SELECTORS.link);

    if (!link || !component.contains(link)) return null;

    return link;
  }

  function setExpandedState(component, expanded) {
    const gridWrap = component.querySelector(SELECTORS.gridWrap);
    const toggle = component.querySelector(SELECTORS.toggle);
    const toggleText = component.querySelector(SELECTORS.toggleText);
    const eligibleCount = parseInt(component.dataset.eligibleCount || "0", 10);

    if (!gridWrap || !toggle || !toggleText) return;

    component.classList.toggle("is-expanded", expanded);
    gridWrap.classList.toggle("is-collapsed", !expanded);
    toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
    toggleText.textContent = expanded ? "Collapse" : `View all ${eligibleCount}`;
  }

  function storeClickedExpansion(component, link, clickedKey) {
    const groupKey = (component.dataset.groupKey || "").trim();
    const handle = (link.getAttribute("data-product-handle") || "").trim();

    if (!groupKey || !handle) return;

    setSessionItem(
      clickedKey,
      JSON.stringify({
        groupKey: groupKey,
        handle: handle,
        timestamp: Date.now()
      })
    );
  }

  function consumeClickedExpansion(component, clickedKey) {
    const rawClickedState = getSessionItem(clickedKey);

    if (!rawClickedState) return false;

    /*
      Remove immediately so the behaviour is genuinely one-shot,
      even if the stored state is stale or invalid.
    */
    removeSessionItem(clickedKey);

    const clickedState = parseClickedState(rawClickedState);

    if (!clickedState) return false;

    const groupKey = (component.dataset.groupKey || "").trim();
    const currentProductHandle = getCurrentProductHandle(component);
    const clickedGroupKey = (clickedState.groupKey || "").trim();
    const clickedHandle = (clickedState.handle || "").trim();
    const clickedTimestamp = Number(clickedState.timestamp) || 0;
    const isRecent =
      clickedTimestamp > 0 && Date.now() - clickedTimestamp <= CLICKED_STATE_TTL_MS;

    return Boolean(
      isRecent &&
        groupKey &&
        currentProductHandle &&
        clickedGroupKey === groupKey &&
        clickedHandle === currentProductHandle
    );
  }

  function initStyleLabel(component) {
    const styleEl = component.querySelector(SELECTORS.style);
    const styleValueEl = component.querySelector(SELECTORS.styleValue);

    if (!styleEl || !styleValueEl) return;

    const defaultStyleValue = styleEl.getAttribute("data-style-default") || "";

    function updateStyleFromElement(element) {
      const label = (element.getAttribute("data-variant-label") || "").trim();

      styleValueEl.textContent = label || defaultStyleValue;
    }

    function resetStyle() {
      styleValueEl.textContent = defaultStyleValue;
    }

    component.addEventListener("pointerover", function (event) {
      const link = getLinkFromEvent(event, component);

      if (!link) return;

      updateStyleFromElement(link);
    });

    component.addEventListener("pointerout", function (event) {
      const link = getLinkFromEvent(event, component);

      if (!link) return;

      if (event.relatedTarget && link.contains(event.relatedTarget)) return;

      resetStyle();
    });

    component.addEventListener("focusin", function (event) {
      const link = getLinkFromEvent(event, component);

      if (!link) return;

      updateStyleFromElement(link);
    });

    component.addEventListener("focusout", function (event) {
      const link = getLinkFromEvent(event, component);

      if (!link) return;

      resetStyle();
    });
  }

  function initProductAlsoAvailable(component) {
    if (!component) return;
    if (component.dataset.alsoAvailableInitialised === "true") return;

    const groupKey = (component.dataset.groupKey || "").trim();

    if (!groupKey) return;

    const isCollapsible = component.hasAttribute("data-collapsible");
    const toggle = component.querySelector(SELECTORS.toggle);
    const clickedKey = getStorageKey(groupKey, "clicked");

    /*
      Legacy cleanup:
      The old implementation stored a persistent expanded/collapsed state.
      This version intentionally does not use that state.
    */
    removeSessionItem(getStorageKey(groupKey, "state"));

    component.dataset.alsoAvailableInitialised = "true";

    if (isCollapsible && toggle) {
      const shouldExpand = consumeClickedExpansion(component, clickedKey);

      setExpandedState(component, shouldExpand);

      toggle.addEventListener("click", function () {
        const isExpanded = component.classList.contains("is-expanded");
        const nextExpanded = !isExpanded;

        setExpandedState(component, nextExpanded);
      });
    } else {
      removeSessionItem(clickedKey);
    }

    component.addEventListener("click", function (event) {
      const link = getLinkFromEvent(event, component);

      if (!link) return;

      storeClickedExpansion(component, link, clickedKey);
    });

    initStyleLabel(component);
  }

  function bootProductAlsoAvailable(root) {
    const scope = root || document;
    const components = scope.querySelectorAll(SELECTORS.component);

    if (!components.length) return;

    components.forEach(initProductAlsoAvailable);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      function () {
        bootProductAlsoAvailable(document);
      },
      { once: true }
    );
  } else {
    bootProductAlsoAvailable(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    bootProductAlsoAvailable(event.target);
  });
})();
